// src/components/RichMessageEditor.jsx
import React, { useState, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Bold,
  Italic,
  List,
  Link,
  Smile,
  Eye,
  Edit2,
  Heading1,
  Heading2,
  Quote,
  Code
} from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery';

/**
 * 🚀 RICH MESSAGE EDITOR - Editor de mensagens com Markdown e preview em tempo real
 * 
 * Características:
 * ✅ Markdown completo com preview
 * ✅ Emojis rápidos
 * ✅ Formatação com botões
 * ✅ Responsivo
 * ✅ Acessível
 * ✅ Performance otimizada com useCallback
 */

export const RichMessageEditor = ({
  value = '',
  onChange,
  placeholder = 'Digite sua mensagem...',
  height = 'auto',
  showToolbar = true,
  maxLength = 2000
}) => {
  const isMobile = useIsMobile();
  const textareaRef = useRef(null);
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'
  const [showEmojis, setShowEmojis] = useState(false);

  // Emojis rápidos
  const emojis = [
    '😊', '👍', '🏠', '💰', '📝', '✅', '🎉', '🤝', '📞', '🔑',
    '⭐', '❤️', '🔥', '👋', '⏰', '📅', '📍', '🔔', '📎', '🎯'
  ];

  // Funções de formatação otimizadas com useCallback
  const insertFormat = useCallback((prefix, suffix = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const before = value.substring(0, start);
    const after = value.substring(end);

    const newText = selectedText
      ? `${before}${prefix}${selectedText}${suffix}${after}`
      : `${before}${prefix}${suffix}${after}`;

    onChange(newText);

    // Restaurar cursor
    setTimeout(() => {
      textarea.focus();
      if (!selectedText) {
        textarea.setSelectionRange(start + prefix.length, start + prefix.length);
      }
    }, 0);
  }, [value, onChange]);

  const insertEmoji = useCallback((emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const before = value.substring(0, start);
    const after = value.substring(start);

    onChange(before + emoji + after);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  }, [value, onChange]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  }, [onChange, maxLength]);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex items-center gap-1 p-2 bg-slate-50 border-b border-slate-200 flex-wrap">
          <button
            onClick={() => insertFormat('**', '**')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Negrito"
          >
            <Bold size={isMobile ? 16 : 18} />
          </button>
          
          <button
            onClick={() => insertFormat('_', '_')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Itálico"
          >
            <Italic size={isMobile ? 16 : 18} />
          </button>
          
          <button
            onClick={() => insertFormat('- ')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Lista"
          >
            <List size={isMobile ? 16 : 18} />
          </button>
          
          <button
            onClick={() => insertFormat('[', '](url)')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Link"
          >
            <Link size={isMobile ? 16 : 18} />
          </button>
          
          <div className="w-px h-5 bg-slate-300 mx-1" />
          
          <button
            onClick={() => insertFormat('# ')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Título 1"
          >
            <Heading1 size={isMobile ? 16 : 18} />
          </button>
          
          <button
            onClick={() => insertFormat('## ')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Título 2"
          >
            <Heading2 size={isMobile ? 16 : 18} />
          </button>
          
          <button
            onClick={() => insertFormat('> ')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Citação"
          >
            <Quote size={isMobile ? 16 : 18} />
          </button>
          
          <button
            onClick={() => insertFormat('`', '`')}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors touch:active:bg-slate-300"
            title="Código"
          >
            <Code size={isMobile ? 16 : 18} />
          </button>
          
          <div className="w-px h-5 bg-slate-300 mx-1" />
          
          <button
            onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')}
            className={`p-1.5 rounded-lg transition-colors touch:active:scale-95 ${
              mode === 'preview'
                ? 'bg-blue-100 text-blue-600'
                : 'hover:bg-slate-200'
            }`}
            title={mode === 'edit' ? 'Visualizar' : 'Editar'}
          >
            {mode === 'edit' ? <Eye size={isMobile ? 16 : 18} /> : <Edit2 size={isMobile ? 16 : 18} />}
          </button>
          
          <button
            onClick={() => setShowEmojis(!showEmojis)}
            className={`p-1.5 rounded-lg transition-colors touch:active:scale-95 ${
              showEmojis ? 'bg-amber-100 text-amber-600' : 'hover:bg-slate-200'
            }`}
            title="Emojis"
          >
            <Smile size={isMobile ? 16 : 18} />
          </button>
        </div>
      )}

      {/* Emoji Picker */}
      {showEmojis && (
        <div className="p-2 bg-white border-b border-slate-200 grid grid-cols-10 gap-1">
          {emojis.map(emoji => (
            <button
              key={emoji}
              onClick={() => {
                insertEmoji(emoji);
                setShowEmojis(false);
              }}
              className="w-8 h-8 hover:bg-slate-100 rounded-lg text-lg flex items-center justify-center touch:active:bg-slate-200"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Editor / Preview */}
      {mode === 'edit' ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`w-full p-3 outline-none resize-none font-mono text-sm ${
            isMobile ? 'min-h-[120px]' : 'min-h-[150px]'
          }`}
          style={{ height }}
        />
      ) : (
        <div className={`p-3 prose prose-sm max-w-none bg-slate-50 min-h-[120px] overflow-auto ${
          isMobile ? 'text-xs' : 'text-sm'
        }`}>
          {value ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-slate-400 italic">Preview vazio</p>
          )}
        </div>
      )}

      {/* Contador de caracteres */}
      {maxLength && (
        <div className="flex justify-end px-3 py-1 text-xs text-slate-400 border-t border-slate-100">
          <span className={value.length > maxLength * 0.9 ? 'text-amber-600' : ''}>
            {value.length}/{maxLength}
          </span>
        </div>
      )}
    </div>
  );
};

export default RichMessageEditor;